import { useChatMessagesContext } from '@/Context/Chat/ChatMessagesContext';
import { useRef } from 'react';

const useMessagesChat = () => {
  const { messages, setMessages, streamingMessage, setStreamingMessage } =
    useChatMessagesContext();

  // Esto genera un mensaje dummy que se usa para indicar que se está enviando un modelo
  const dummyMessage = useRef(null);

  // Mensaje del usuario
  const userMessage = useRef(null);

  // Message position
  const position = useRef(null);
  const finished = useRef(null);

  // Agrega el contenido de stop a los mensajes
  const addContentStop = () => {
    const transformStreamingMessage = Array.isArray(streamingMessage)
      ? streamingMessage.map((message) => ({
          ...message,
          finished: true,
        }))
      : {
          ...streamingMessage,
          finished: true,
        };
    setMessages((prevMessages) => [transformStreamingMessage, ...prevMessages]);
    setStreamingMessage(null);
  };

  /**
   * Limpia los mensajes del chat.
   */
  const clearMessages = () => {
    dummyMessage.current = null;
    setMessages([]);
  };

  /**
   * Elimina el ultimo mensaje del chat.
   */
  const removeLastMessage = () => {
    setMessages((prevMessages) => {
      const newMessages = [...prevMessages];
      newMessages.shift();
      return newMessages;
    });
  };

  /**
   * Agrega lista de mensajes al chat.
   */
  const addChatMessages = (messages) => {
    dummyMessage.current = null;
    setMessages((prevMessages) => [...prevMessages, ...messages]);
  };

  /**
   * Agrega un nuevo mensaje al chat.
   */
  const addMessage = (message) => {
    setMessages((prevMessages) => [
      Array.isArray(message) ? [...message] : message,
      ...prevMessages,
    ]);
  };

  /**
   * Check if the model already has a message.
   */
  const getMessage = (singleModel, messages = null) => {
    let dummy = {
      model: singleModel.abbreviation,
      modelRef: singleModel._id,
      data: '',
      finished: false,
    };

    if (messages) {
      const messageArray = Array.isArray(messages) ? messages : [messages];
      const content = messageArray.find((m) => m.modelRef == singleModel._id);
      if (content) dummy = content;
    }

    return dummy;
  };

  /**
   * Crea un objeto de mensaje dummy con la estructura requerida
   * @param {Object|Array} model - Objeto del modelo con abbreviation y _id o array de modelos
   * @returns {Object|Array} Objeto mensaje dummy o array de mensajes dummy
   */
  const createDummyMessage = (model, messages = null) => {
    if (Array.isArray(model)) {
      return model.map((singleModel) => getMessage(singleModel, messages));
    }

    return getMessage(model, messages);
  };

  /**
   * Actualiza el mensaje dummy. (Es cuando apenas se va enviar un modelo)
   */
  const updateDummyMessage = (
    models,
    messages = null,
    setStreaming = false
  ) => {
    const modelsArray = models ?? [];

    if (modelsArray.length === 0) {
      dummyMessage.current = null;
    } else {
      dummyMessage.current = createDummyMessage(modelsArray, messages);
    }

    if (setStreaming) setStreamingMessage(dummyMessage.current);
    position.current = null;
  };

  /**
   * Actualiza los mensajes en tiempo real sin forzar un re-render innecesario.
   */
  const updateStreamingMessages = (message, loading) => {
    if (loading) return;
    if (!message.finished) finished.current = null;

    setStreamingMessage((prev) => {
      if (position.current && position.current !== 0) {
        if (message.finished) {
          position.current = null;
          finished.current = true;
        }
        return prev;
      }

      if (finished.current) {
        return prev;
      }

      if (!prev) {
        return { ...message };
      }

      if (Array.isArray(prev)) {
        // Mantener el array existente y solo actualizar el mensaje específico
        return prev.map((m) =>
          m.modelRef === message.modelRef ? { ...message } : m
        );
      }

      // Si el mensaje anterior no es un array, retornar el nuevo mensaje
      return { ...message };
    });
  };

  // Remplaza el ultimo mensaje del chat cuando el servicio de streaming termina
  const replaceFirstMessage = (message) => {
    setMessages((prevMessages) => [message, ...prevMessages]);
    setStreamingMessage(null);
  };

  const replaceMessage = (message, pos) => {
    if (pos == 0) {
      replaceFirstMessage(message);
      return;
    }

    position.current = pos;
    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      updatedMessages[pos] = message;
      return updatedMessages;
    });
  };

  return {
    dummyMessage,
    userMessage,
    messages,
    setMessages,
    addMessage,
    updateStreamingMessages,
    replaceFirstMessage,
    replaceMessage,
    addChatMessages,
    updateDummyMessage,
    clearMessages,
    removeLastMessage,
    addContentStop,
    createDummyMessage,
  };
};

export default useMessagesChat;
